import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { Router, useNavigate } from "react-router-dom";
import IosSwitchMain from "../../../components/iosSwitchMain";
import {
  getFromStorage,
  removeFromStorage,
  setToStorage,
  STORAGE_KEYS,
} from "../../../helpers";
import { showError, showToast } from "../../../constants/toasts";
import { Offer, OfferData } from "../../../types/General";
import { isNumber, isValidNumber } from "../../../utils/validations";
import { useLazyGetPreviousQuestionQuery } from "../../../service/user";
import { OFFER_STATUS, QUESTION_TYPE } from "../../../utils/enums";
import dayjs from "dayjs";
import {
  useCreateOfferMutation,
  useLazyCloneOfferQuery,
  useUpdateOfferMutation,
} from "../../../service/offer";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
interface OthersFormProps {
  alignment: string;
  offerDetails: Offer | null;
  updateId: string | undefined;
  isClone?: boolean;
  tabs: number;
  setTabs: Dispatch<SetStateAction<number>>;
  categoryTab: any;
  status: any;
  categoryName: string;
  categoryId: string;
}

interface QuestionOption {
  optionText: string;
}

interface QuestionData {
  id?: any;
  label?: string;
  question?: string;
  inputType: string; // e.g., "text", "checkbox"
  isReused: boolean;
  options: QuestionOption[];
}

function ResturantForm({
  alignment,
  offerDetails,
  updateId,
  isClone,
  tabs,
  setTabs,
  categoryId,
  categoryName,
  categoryTab,
  status,
}: OthersFormProps) {
  const navigate = useNavigate();
  const [getQuestions] = useLazyGetPreviousQuestionQuery();
  const [createOffer] = useCreateOfferMutation();
  const [updateOffer] = useUpdateOfferMutation();
  const [cloneOffer] = useLazyCloneOfferQuery();

  const [isCancellationEnabled, setIsCancellationEnabled] = useState(false);
  const [valueTabs, setValueTabs] = useState(0);
  const [questionTabs, setQuestionTabs] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState("Week");
  const [bookingPeriod, setBookingPeriod] = useState("");
  const [cancellationAmount, setCancellationAmount] = useState("");
  const [cancellationHours, setCancellationHours] = useState("");

  const [textQuestions, setTextQuestions] = useState<QuestionData[]>([]);
  const [checkboxQuestions, setCheckboxQuestions] = useState<QuestionData[]>(
    []
  );
  const [radioQuestions, setRadioQuestions] = useState<QuestionData[]>([]);
  const [dateQuestions, setDateQuestions] = useState<QuestionData[]>([]);
  const [textAreaQuestions, setTextAreaQuestions] = useState<QuestionData[]>(
    []
  );

  const offerData = getFromStorage(STORAGE_KEYS.offerData);
  const parsedData: OfferData | null = offerData
    ? (JSON.parse(offerData) as OfferData)
    : null;

  const handleChangePeriod = (event: SelectChangeEvent) => {
    const { value } = event.target;
    setSelectedPeriod(value);
  };

  const [value, setValue] = useState<QuestionData>({
    inputType: "",
    question: "",
    options: [],
    isReused: false,
  });

  const [currentOption, setCurrentOption] = useState<string>("");
  const [questionsList, setQuestionsList] = useState<QuestionData[]>([]);
  const [error, setError] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openPrevQModal, setOpenPrevQModal] = useState(false);

  const match = offerDetails?.advanceBookingPeriod?.match(/(\d+)\s*(\w+)/);

  const handleCheckboxChange = (q: any, checked: boolean) => {
    const questionData: QuestionData = {
      question: q?.label,
      inputType: q?.inputType,
      isReused: true, // or false depending on your requirement
      options: q?.options || [],
    };

    if (checked) {
      setSelectedQuestions((prev) => [...prev, questionData]);
    } else {
      setSelectedQuestions((prev) =>
        prev.filter((item) => item?.question !== q?.label)
      );
    }
  };

  const handleSaveQuestion = () => {
    if (!selectedQuestions?.length) {
      showError("Please select questions");
      return;
    }

    const merged = [...questionsList, ...selectedQuestions];

    // remove duplicates by question text
    const unique = merged?.filter(
      (q, index, self) =>
        index === self?.findIndex((x) => x?.question === q?.question)
    );
    setValueTabs(0);
    setQuestionsList(unique);
    setOpenPrevQModal(false);
  };

  const handleChangeType = (e: SelectChangeEvent<string>) => {
    setValue({
      ...value,
      inputType: e.target.value,
      options: [], // reset options when type changes
    });
  };

  const handleAddOption = () => {
    if (currentOption.trim()) {
      setValue((prev) => ({
        ...prev,
        options: [...prev.options, { optionText: currentOption.trim() }],
      }));
      setCurrentOption("");
    }
  };

  const handleSave = () => {
    if (!value?.question?.trim() || !value?.inputType) {
      showError("Please enter a question");
      return;
    }

    const formattedQuestion: QuestionData = {
      question: value?.question?.trim(),
      inputType:
        value?.inputType === "Input" ? "text" : value?.inputType?.toLowerCase(),
      isReused: value?.isReused,
      options: value?.options, // already [{ optionText: string }]
    };

    setQuestionsList((prev) => [...prev, formattedQuestion]);

    setValue({
      question: "",
      inputType: "",
      isReused: false,
      options: [],
    });
    setCurrentOption("");
  };

  const handleEditQuestion = (idx: number) => {
    const q = questionsList[idx];
    setValue({
      question: q.question || q.label || "",
      inputType:
        q.inputType === "text"
          ? "Input"
          : q.inputType.charAt(0).toUpperCase() + q.inputType.slice(1),
      isReused: q.isReused,
      options: q.options || [],
    });
    setQuestionsList((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteQuestion = (idx: number) => {
    setQuestionsList((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = () => {
    setError(true);

    if (!bookingPeriod) {
      return;
    }

    if (isCancellationEnabled && (!cancellationAmount || !cancellationHours)) {
      return;
    }

    // if (!questionsList?.length) {
    //   showError("Please add questions");
    //   return;
    // }

    const newBody = {
      AdvanceBookingPeriod: `${bookingPeriod} ${selectedPeriod}`,
      bookingPeriod: bookingPeriod,
      selectedPeriod: selectedPeriod,
      IsCancellationPolicy: isCancellationEnabled,
      QuestionJson: questionsList,
      ...(isCancellationEnabled
        ? { CancellationAmountPerPerson: cancellationAmount || "" }
        : {}),
      ...(isCancellationEnabled
        ? { CancellationHours: cancellationHours || "" }
        : {}),
      IsActive: alignment === OFFER_STATUS.ACTIVE ? true : false,
      IsArchive: alignment === OFFER_STATUS.ARCHIVE ? true : false,
    };

    const prevData = getFromStorage(STORAGE_KEYS.offerData);
    let parsedPrevData = {};

    if (prevData) {
      try {
        parsedPrevData = JSON.parse(prevData);
      } catch (err) {
        console.error("Error parsing stored offer data:", err);
      }
    }

    // Merge old and new data
    const mergedData = {
      ...parsedPrevData,
      ...newBody,
    };

    setToStorage(STORAGE_KEYS.offerData, JSON.stringify(mergedData));

    navigate("/manage-offer/preview-offer?mode=EVENT", {
      state: {
        updateId,
        isClone,
        status,
        categoryTab,
        categoryId,
        categoryName,
      },
    });
  };

  const handleArchive = async () => {
    const formData = new FormData();

    const appendIfPresent = (key: string, value: any) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    };

    const appendArray = (key: string, arr?: any[]) => {
      if (Array.isArray(arr)) {
        arr.forEach((item) => {
          if (item !== undefined && item !== null && item !== "") {
            formData.append(key, item);
          }
        });
      }
    };

    appendIfPresent("Title", parsedData?.Title);
    appendIfPresent("Badge", parsedData?.Badge);
    appendIfPresent("Description", parsedData?.Description);
    appendIfPresent("Subtitle", parsedData?.Subtitle);
    appendIfPresent("CategoryId", parsedData?.CategoryId);
    appendIfPresent("OfferType", parsedData?.OfferType);

    appendIfPresent(
      "OfferDisplayStartDate",
      parsedData?.OfferDisplayStartDate
        ? dayjs(parsedData?.OfferDisplayTime).toISOString()
        : null
    );

    appendIfPresent(
      "OfferDisplayEndDate",
      parsedData?.OfferDisplayEndDate
        ? dayjs(parsedData?.OfferDisplayEndTime).toISOString()
        : null
    );

    appendIfPresent(
      "IsButterflyEffect",
      parsedData?.IsButterflyEffect ? "true" : "false"
    );

    appendIfPresent(
      "ButterflyEffectStartDate",
      parsedData?.ButterflyEffectStartDate
        ? dayjs(parsedData?.ButterflyEffectStartTime).toISOString()
        : null
    );

    appendIfPresent(
      "ButterflyEffectEndDate", // fixed the duplicate key name
      parsedData?.ButterflyEffecteEndDate
        ? dayjs(parsedData?.ButterflyEffectEndTime).toISOString()
        : null
    );

    appendIfPresent("ButtonName", parsedData?.ButtonName);
    appendArray("GroupsId", parsedData?.GroupsId);
    appendArray("TagsId", parsedData?.TagsId);
    appendArray("offerImages", parsedData?.offerImages);
    appendIfPresent(
      "AdvanceBookingPeriod",
      `${bookingPeriod} ${selectedPeriod}`
    );
    appendIfPresent(
      "IsCancellationPolicy",
      isCancellationEnabled ? "true" : "false"
    );
    appendIfPresent("CancellationAmountPerPerson", cancellationAmount);
    appendIfPresent("CancellationHours", cancellationHours);
    appendIfPresent("QuestionJson", JSON.stringify(questionsList || []));
    appendIfPresent(
      "OfferDisplayTime",
      parsedData?.OfferDisplayTime
        ? dayjs(parsedData?.OfferDisplayTime).format("HH:mm:ss")
        : null
    );

    appendIfPresent(
      "OfferDisplayEndTime",
      parsedData?.OfferDisplayEndTime
        ? dayjs(parsedData?.OfferDisplayEndTime).format("HH:mm:ss")
        : null
    );
    appendIfPresent(
      "ButterflyEffectStartTime",
      parsedData?.ButterflyEffectStartTime
        ? dayjs(parsedData?.ButterflyEffectStartTime).format("HH:mm:ss")
        : null
    );
    appendIfPresent(
      "ButterflyEffectEndTime",
      parsedData?.ButterflyEffectEndTime
        ? dayjs(parsedData?.ButterflyEffectEndTime).format("HH:mm:ss")
        : null
    );
    appendIfPresent(
      "IsActive",
      alignment === OFFER_STATUS.ACTIVE ? "true" : "false"
    );
    appendIfPresent(
      "IsArchive",
      alignment === OFFER_STATUS.ARCHIVE ? "true" : "false"
    );

    appendIfPresent("OfferId", updateId);

    if (!updateId || isClone) {
      try {
        setLoading(true);
        const response = await createOffer(formData).unwrap();
        setLoading(false);
        if (response?.status === 200) {
          removeFromStorage(STORAGE_KEYS.offerData);
          // navigate("/manage-offer/preview-offer-detail");
          showToast(response?.message || "");
          navigate("/manage-offer", {
            state: { status, categoryTab, categoryId, categoryName },
          });
        } else {
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    } else {
      try {
        setLoading(true);
        const response = await updateOffer(formData).unwrap();
        setLoading(false);
        if (response?.status === 200) {
          removeFromStorage(STORAGE_KEYS.offerData);
          // navigate("/manage-offer/preview-offer-detail");
          showToast(response?.message || "");
          navigate("/manage-offer", {
            state: { status, categoryTab, categoryId, categoryName },
          });
        } else {
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    }
  };

  const cloneOfferApi = async () => {
    try {
      const response = await cloneOffer({ offerId: updateId }).unwrap();
      if (response.status === 200) {
        removeFromStorage(STORAGE_KEYS.offerData);
        // navigate("/manage-offer/preview-offer-detail");
        showToast(response?.message || "");
        navigate("/manage-offer", {
          state: { status: 3, categoryTab, categoryId, categoryName },
        });
      } else {
        showError(response?.message || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    setBookingPeriod(parsedData?.bookingPeriod ?? match?.[1] ?? "");
    setSelectedPeriod(parsedData?.selectedPeriod ?? match?.[2] ?? "Week");
    setIsCancellationEnabled(
      parsedData?.IsCancellationPolicy ??
        offerDetails?.isCancellationPolicy ??
        false
    );
    setCancellationAmount(
      parsedData?.CancellationAmountPerPerson
        ? parsedData?.CancellationAmountPerPerson
        : offerDetails?.cancellationAmountPerPerson
          ? String(offerDetails?.cancellationAmountPerPerson)
          : ""
    );
    setCancellationHours(
      parsedData?.CancellationHours
        ? parsedData?.CancellationHours
        : offerDetails?.cancellationHours
          ? String(offerDetails?.cancellationHours)
          : ""
    );
    setQuestionsList(
      parsedData?.QuestionJson?.length
        ? parsedData?.QuestionJson
        : offerDetails?.questions
          ? offerDetails?.questions?.map((q) => ({
              question: q?.label,
              inputType: q?.inputType?.toLowerCase(),
              isReused: q?.isReused,
              options: q?.options ?? [],
            }))
          : []
    );
  }, []);

  const getPreviousQuestions = async () => {
    try {
      const response = await getQuestions({
        page: 1,
        search: "",
      }).unwrap();
      if (response.status === 200) {
        setCheckboxQuestions(
          response?.data?.filter(
            (q: any) => q?.inputType === QUESTION_TYPE.CHECKBOX
          )
        );
        setTextQuestions(
          response?.data?.filter(
            (q: any) => q?.inputType === QUESTION_TYPE.TEXT
          )
        );
        setRadioQuestions(
          response?.data?.filter(
            (q: any) => q?.inputType === QUESTION_TYPE.RADIO
          )
        );
        setTextAreaQuestions(
          response?.data?.filter(
            (q: any) => q?.inputType === QUESTION_TYPE.TEXTAREA
          )
        );
        setDateQuestions(
          response?.data?.filter(
            (q: any) => q?.inputType === QUESTION_TYPE.DATE
          )
        );
      } else {
        setCheckboxQuestions([]);
        setTextQuestions([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const handleChangeTab = (e: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);

    if (newValue === 1) {
      setOpenPrevQModal(true);
    }
  };

  useEffect(() => {
    getPreviousQuestions();
  }, []);

  return (
    <>
      <div className="section_title mn_hdng">
        <h2>Restaurant</h2>

        <div className="btn_flex flx_end">
          <Button className="btn btn_primary" onClick={() => setTabs(0)}>
            Back
          </Button>

          {alignment === "archived" && (
            <Button
              className="btn btn_primary"
              onClick={() => {
                if (updateId) {
                  cloneOfferApi();
                } else {
                  handleArchive();
                }
              }}
            >
              Save & Exit
            </Button>
          )}
          <Button className="btn btn_primary" onClick={handleSubmit}>
            Preview
          </Button>
        </div>
      </div>

      <div className="gap_m avnc_bkng">
        <div className="control_group w_100">
          <label htmlFor="title">Advance Booking Period</label>
          <div className="gap_m">
            <div className="advnc control_group w_50">
              <TextField
                placeholder="Booking Period"
                hiddenLabel
                fullWidth
                value={bookingPeriod}
                id="title"
                name="title"
                className="text_field"
                inputProps={{ maxLength: 5 }}
                onChange={(val) => {
                  const { value } = val.target;
                  if (isNumber(value)) {
                    setBookingPeriod(value);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" className="size_big">
                      <Select
                        className="dropse"
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        inputProps={{
                          "aria-label": "Without label",
                        }}
                        displayEmpty
                        value={selectedPeriod}
                        name="page"
                        onChange={handleChangePeriod}
                      >
                        <MenuItem value="" disabled>
                          Select
                        </MenuItem>

                        <MenuItem value="Week">Weekly</MenuItem>
                        <MenuItem value="Month">Monthly </MenuItem>
                      </Select>
                    </InputAdornment>
                  ),
                }}
              />
              {error && !bookingPeriod ? (
                <p className="err_msg">Advance booking period is required</p>
              ) : (
                ""
              )}
            </div>

            <div className="cncl_plcy control_group w_50">
              <label>Cancellation Policy</label>
              <IosSwitchMain
                checked={isCancellationEnabled}
                onChange={(e) => setIsCancellationEnabled(e.target.checked)}
              />
            </div>
          </div>
        </div>
        {isCancellationEnabled && (
          <>
            <div className="control_group w_50">
              <label htmlFor="cancellationAmount">
                Cancellation Amount Per Person
              </label>
              <TextField
                placeholder="Enter cancellation charges"
                id="cancellationAmount"
                hiddenLabel
                fullWidth
                value={cancellationAmount}
                inputProps={{ maxLength: 5 }}
                onChange={(e) => {
                  const { value } = e.target;
                  if (isValidNumber(value)) {
                    setCancellationAmount(value);
                  }
                }}
              />
              {error && !cancellationAmount ? (
                <p className="err_msg">Cancellation amount is required</p>
              ) : (
                ""
              )}
            </div>
            <div className="control_group w_50">
              <label htmlFor="cancellationHours">Cancellation Hours</label>
              <TextField
                placeholder="Enter hours"
                id="cancellationHours"
                hiddenLabel
                fullWidth
                value={cancellationHours}
                inputProps={{ maxLength: 5 }}
                onChange={(e) => {
                  const { value } = e.target;
                  if (isValidNumber(value)) {
                    setCancellationHours(value);
                  }
                }}
              />
              {error && !cancellationHours ? (
                <p className="err_msg">Cancellation hours is required</p>
              ) : (
                ""
              )}
            </div>
          </>
        )}
        <Box className="custom_tabs2 mt_40 mb_30" sx={{ width: "100%" }}>
          <Tabs
            aria-label="basic tabs example"
            className="custom_tabs_links"
            value={valueTabs}
            onChange={handleChangeTab}
          >
            <Tab {...a11yProps(0)} label="+ Add Custom" />
            <Tab {...a11yProps(1)} label="+ Add Previous Questions" />
          </Tabs>
        </Box>
        <div>
          <div className="control_group w_100 cstm_mn">
            <div className="gap_m">
              <div className="control_group w_50">
                <label htmlFor="demo-simple-select">Select Type</label>

                <Select
                  className="dropse"
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                  displayEmpty
                  name="inputType"
                  value={value.inputType}
                  onChange={handleChangeType}
                >
                  <MenuItem value="" disabled>
                    Select Type
                  </MenuItem>
                  <MenuItem value="Checkbox">Checkbox</MenuItem>
                  <MenuItem value="Input">Input</MenuItem>
                  <MenuItem value="TextArea">TextArea</MenuItem>
                  <MenuItem value="Radio">Radio</MenuItem>
                  <MenuItem value="Date">Date</MenuItem>
                </Select>
              </div>

              <div className="control_group w_50">
                <label htmlFor="question">Enter Question</label>
                <TextField
                  placeholder="Question"
                  id="question"
                  hiddenLabel
                  fullWidth
                  value={value.question}
                  onChange={(e) =>
                    setValue((prev) => ({
                      ...prev,
                      question: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>
          {value.inputType === "Checkbox" || value.inputType === "Radio" ? (
            <div className="control_group w_100">
              <label htmlFor="optionText">Add Options</label>
              <TextField
                placeholder="Option Text"
                id="optionText"
                hiddenLabel
                fullWidth
                value={currentOption}
                onChange={(e) => setCurrentOption(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddOption();
                  }
                }}
              />
            </div>
          ) : null}

          {value.options.length > 0 && (
            <FormGroup style={{ marginTop: 10 }}>
              {value.options.map((opt, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontSize: 14, marginRight: 8 }}>{i + 1}.</span>
                  <span>{opt?.optionText}</span>
                </div>
              ))}
            </FormGroup>
          )}

          <div className="control_group w_100 chkbx_mn">
            <FormControlLabel
              control={
                <Checkbox
                  checked={value.isReused}
                  onChange={(e) =>
                    setValue((prev) => ({
                      ...prev,
                      isReused: e.target.checked,
                    }))
                  }
                />
              }
              name="remember"
              label="Save this question for other offers"
            />
            {value.inputType === "Checkbox" || value.inputType === "Radio" ? (
              <Button className="btn btn_primary" onClick={handleAddOption}>
                + Add Option
              </Button>
            ) : null}
          </div>

          <Button
            className="btn btn_primary"
            sx={{ marginBottom: "20px" }}
            onClick={handleSave}
          >
            Save
          </Button>

          {questionsList?.length > 0 && (
            <div style={{ marginTop: "10px" }}>
              <h2 style={{ marginBottom: "20px" }}>Saved Questions</h2>
              {questionsList?.map((q: any, idx) => (
                <div
                  key={idx}
                  style={{
                    marginBottom: 10,
                    borderRadius: 8,
                    padding: "8px 12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <strong style={{ fontSize: "16px" }}>
                        {`${idx + 1}. ${q?.question || q?.label}`}
                      </strong>{" "}
                      ({q?.inputType})
                    </div>
                    <div>
                      <IconButton
                        className="edit_icon"
                        onClick={() => handleEditQuestion(idx)}
                      >
                        <img src="/static/images/edit2_icon.svg" alt="icon" />
                      </IconButton>

                      <IconButton
                        className="edit_icon"
                        onClick={() => handleDeleteQuestion(idx)}
                      >
                        <img
                          src="/static/images/trash2_icon.svg"
                          alt="Delete"
                        />
                      </IconButton>
                    </div>
                  </div>

                  {(q.inputType.toLowerCase() === "checkbox" ||
                    q.inputType.toLowerCase() === "radio") && (
                    <ul className="options_list">
                      {q?.options?.map((opt: any, i: number) => (
                        <li
                          key={i}
                          style={{ marginLeft: 30, marginTop: 5 }}
                          className="option_item"
                        >
                          {opt?.optionText}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <Modal
          className="modal commissionOffer_modal build_offer_modal"
          open={openPrevQModal}
          onClose={() => {
            setValueTabs(0);
            setOpenPrevQModal(false);
          }}
        >
          <div className="modal-dialog" style={{ minWidth: "90%" }}>
            <div className="modal-body">
              <div className="modal_title text_center mb_30">
                <h2>Add Previous Questions</h2>

                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <div className="btn_flex flx_end">
                    <Button
                      className="btn btn_primary"
                      onClick={handleSaveQuestion}
                    >
                      Save
                    </Button>
                  </div>

                  <div
                    role="button"
                    tabIndex={0}
                    className="btn-close"
                    onClick={() => {
                      setOpenPrevQModal(false);
                      setValueTabs(0);
                    }}
                  >
                    <CloseIcon />
                  </div>
                </div>
              </div>
              <div className="control_group w_100">
                <CustomTabPanel value={valueTabs} index={1}>
                  <div className="cards crds_mn">
                    <Box className="custom_tabs2  mb_30" sx={{ width: "100%" }}>
                      <Tabs
                        aria-label="basic tabs example"
                        className="custom_tabs_links"
                        value={questionTabs}
                        onChange={(e, newValue) => setQuestionTabs(newValue)}
                      >
                        <Tab {...a11yProps(0)} label="Checkbox" />
                        <Tab {...a11yProps(1)} label="Text" />
                        <Tab {...a11yProps(2)} label="Radio" />
                        <Tab {...a11yProps(3)} label="TextArea" />
                        <Tab {...a11yProps(4)} label="Date" />
                      </Tabs>
                    </Box>

                    <CustomTabPanel value={questionTabs} index={0}>
                      <FormGroup className="prvs_qstn_mn">
                        {checkboxQuestions?.length > 0 && (
                          <div className="d_flx1">
                            {checkboxQuestions?.map((q: any, idx) => (
                              <div key={idx} className="question_card">
                                {/* Checkbox + Title */}
                                <div className="question_header">
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={selectedQuestions.some(
                                          (item) => item?.question === q?.label
                                        )}
                                        onChange={(e) =>
                                          handleCheckboxChange(
                                            q,
                                            e.target.checked
                                          )
                                        }
                                      />
                                    }
                                    label={
                                      <h3 className="question_title">
                                        Question {idx + 1}
                                      </h3>
                                    }
                                  />
                                </div>

                                {/* Question Text */}
                                <p className="question_label">
                                  <strong>{q?.label}</strong>{" "}
                                  <span className="input_type">
                                    ({q.inputType})
                                  </span>
                                </p>

                                {/* Options List */}
                                {q?.options?.length > 0 && (
                                  <ul className="options_list">
                                    {q?.options?.map(
                                      (opt: QuestionOption, i: number) => (
                                        <li key={i} className="option_item">
                                          {opt?.optionText}
                                        </li>
                                      )
                                    )}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </FormGroup>
                    </CustomTabPanel>

                    <CustomTabPanel value={questionTabs} index={1}>
                      <FormGroup className="prvs_qstn_mn">
                        <div className="d_flx1">
                          {textQuestions.map((q: any, i) => (
                            <div key={i} className="question_card">
                              <div className="question_header">
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedQuestions.some(
                                        (item) => item.question === q?.label
                                      )}
                                      onChange={(e) =>
                                        handleCheckboxChange(
                                          q,
                                          e.target.checked
                                        )
                                      }
                                    />
                                  }
                                  label={<h2>Question {i + 1}</h2>}
                                />
                              </div>
                              <p>{q?.label}</p>
                            </div>
                          ))}
                        </div>
                      </FormGroup>
                    </CustomTabPanel>

                    <CustomTabPanel value={questionTabs} index={2}>
                      <FormGroup className="prvs_qstn_mn">
                        {radioQuestions?.length > 0 && (
                          <div className="d_flx1">
                            {radioQuestions?.map((q: any, idx) => (
                              <div className="question_card">
                                <div className="question_header">
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={selectedQuestions?.some(
                                          (item) => item.question === q?.label
                                        )}
                                        onChange={(e) =>
                                          handleCheckboxChange(
                                            q,
                                            e.target.checked
                                          )
                                        }
                                      />
                                    }
                                    label={<h2>Question {idx + 1}</h2>}
                                  />
                                </div>
                                <p style={{ fontSize: "16px" }}>{q?.label}</p> (
                                {q.inputType})
                                <ul style={{ marginTop: "10px" }}>
                                  {q?.options?.map(
                                    (opt: QuestionOption, i: number) => (
                                      <li style={{ marginLeft: 20 }} key={i}>
                                        {opt?.optionText}
                                      </li>
                                    )
                                  )}
                                </ul>
                              </div>
                            ))}
                          </div>
                        )}
                      </FormGroup>
                    </CustomTabPanel>

                    <CustomTabPanel value={questionTabs} index={3}>
                      <FormGroup className="prvs_qstn_mn">
                        <div className="d_flx1">
                          {textAreaQuestions?.map((q: any, i) => (
                            <div key={i} className="question_card">
                              <div className="question_header">
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedQuestions?.some(
                                        (item) => item?.question === q?.label
                                      )}
                                      onChange={(e) =>
                                        handleCheckboxChange(
                                          q,
                                          e.target.checked
                                        )
                                      }
                                    />
                                  }
                                  label={<h2>Question {i + 1}</h2>}
                                />
                              </div>
                              <p>{q?.label}</p>
                            </div>
                          ))}
                        </div>
                      </FormGroup>
                    </CustomTabPanel>

                    <CustomTabPanel value={questionTabs} index={4}>
                      <FormGroup className="prvs_qstn_mn">
                        <div className="d_flx1">
                          {dateQuestions?.map((q: any, i) => (
                            <div key={i} className="question_card">
                              <div className="question_header">
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedQuestions?.some(
                                        (item) => item?.question === q?.label
                                      )}
                                      onChange={(e) =>
                                        handleCheckboxChange(
                                          q,
                                          e.target.checked
                                        )
                                      }
                                    />
                                  }
                                  label={<h2>Question {i + 1}</h2>}
                                />
                              </div>
                              <p>{q?.label}</p>
                            </div>
                          ))}
                        </div>
                      </FormGroup>
                    </CustomTabPanel>
                  </div>
                </CustomTabPanel>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
}

export default ResturantForm;
